import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import traceApi from '../../services/api/trace.api';
import {
  ArrowLeftIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ServerIcon,
  CircleStackIcon,
} from '@heroicons/react/24/outline';

interface Span {
  id: string;
  span_id: string;
  parent_span_id?: string;
  operation_name: string;
  span_kind: string;
  start_time: string;
  duration_us?: number;
  status_code: string;
  status_message?: string;
  attributes?: Record<string, any>;
  events?: any[];
  resource_attributes?: Record<string, any>;
}

interface Trace {
  id: string;
  trace_id: string;
  operation_name: string;
  timestamp: number;
  duration_ms: number;
  status: string;
  error_count: number;
  span_count: number;
  tags?: Record<string, any>;
}

export default function TraceDetailsPage() {
  const { applicationId, traceId } = useParams<{ applicationId: string; traceId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('apm');

  const [trace, setTrace] = useState<Trace | null>(null);
  const [spans, setSpans] = useState<Span[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSpans, setExpandedSpans] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadTraceDetails();
  }, [applicationId, traceId]);

  const loadTraceDetails = async () => {
    if (!applicationId || !traceId) return;

    setIsLoading(true);
    setError(null);

    try {
      const [traceResponse, spansResponse] = await Promise.all([
        traceApi.getById(applicationId, traceId),
        traceApi.getSpans(traceId),
      ]);

      if (traceResponse.success) {
        setTrace(traceResponse.data);
      }

      if (spansResponse.success) {
        const sortedSpans = sortSpansForWaterfall(spansResponse.data);
        setSpans(sortedSpans);
      }
    } catch (err: any) {
      console.error('Error loading trace details:', err);
      setError(err.message || 'Failed to load trace details');
    } finally {
      setIsLoading(false);
    }
  };

  // Sort spans for waterfall display: parent spans before children
  const sortSpansForWaterfall = (spans: Span[]): Span[] => {
    const spanMap = new Map<string, Span>();
    const rootSpans: Span[] = [];
    const childrenMap = new Map<string, Span[]>();

    // Build maps
    spans.forEach(span => {
      spanMap.set(span.span_id, span);
      if (!span.parent_span_id) {
        rootSpans.push(span);
      } else {
        if (!childrenMap.has(span.parent_span_id)) {
          childrenMap.set(span.parent_span_id, []);
        }
        childrenMap.get(span.parent_span_id)!.push(span);
      }
    });

    // Recursively build sorted list
    const sorted: Span[] = [];
    const addSpanWithChildren = (span: Span, depth: number = 0) => {
      sorted.push({ ...span, depth } as any);
      const children = childrenMap.get(span.span_id) || [];
      children
        .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
        .forEach(child => addSpanWithChildren(child, depth + 1));
    };

    rootSpans
      .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
      .forEach(root => addSpanWithChildren(root));

    return sorted;
  };

  const toggleSpan = (spanId: string) => {
    setExpandedSpans(prev => {
      const next = new Set(prev);
      if (next.has(spanId)) {
        next.delete(spanId);
      } else {
        next.add(spanId);
      }
      return next;
    });
  };

  const formatDuration = (durationUs?: number) => {
    if (!durationUs) return 'N/A';
    const ms = durationUs / 1000;
    if (ms < 1) return `${durationUs}μs`;
    if (ms < 1000) return `${ms.toFixed(2)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getStatusColor = (status: string) => {
    if (status === 'error') return 'text-red-600 dark:text-red-400';
    if (status === 'ok') return 'text-green-600 dark:text-green-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const getStatusIcon = (status: string) => {
    if (status === 'error') return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
    if (status === 'ok') return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    return <ClockIcon className="h-5 w-5 text-gray-500" />;
  };

  const getSpanColor = (spanKind: string) => {
    switch (spanKind) {
      case 'server': return 'bg-blue-100 border-blue-400 dark:bg-blue-900/30';
      case 'client': return 'bg-green-100 border-green-400 dark:bg-green-900/30';
      case 'internal': return 'bg-gray-100 border-gray-400 dark:bg-gray-800/30';
      case 'producer': return 'bg-purple-100 border-purple-400 dark:bg-purple-900/30';
      case 'consumer': return 'bg-yellow-100 border-yellow-400 dark:bg-yellow-900/30';
      default: return 'bg-gray-100 border-gray-400 dark:bg-gray-800/30';
    }
  };

  const calculateWaterfallPosition = (span: Span) => {
    if (!trace) return { left: 0, width: 0 };

    const traceStart = trace.timestamp;
    const traceDuration = trace.duration_ms * 1000; // Convert to microseconds
    const spanStart = new Date(span.start_time).getTime() * 1000; // Convert to microseconds
    const spanDuration = span.duration_us || 0;

    const left = ((spanStart - traceStart) / traceDuration) * 100;
    const width = (spanDuration / traceDuration) * 100;

    return { left: Math.max(0, left), width: Math.max(0.5, width) };
  };

  const isDatabase = (span: Span) => {
    return span.attributes?.['db.system'] || span.operation_name.toLowerCase().includes('db.');
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            <p className="text-gray-600 dark:text-gray-400">{t('apm:traces.details.loading')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error || !trace) {
    return (
      <MainLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">{t('apm:traces.details.error')}</h3>
            <p className="text-red-600 dark:text-red-400">{error || t('apm:traces.details.notFound')}</p>
            <button
              onClick={() => navigate(`/apm/traces`)}
              className="mt-4 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 font-medium"
            >
              ← {t('apm:traces.details.backToTraces')}
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/apm/traces`)}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            {t('apm:traces.details.backToTraces')}
          </button>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{trace.operation_name}</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">Trace ID: {trace.trace_id}</p>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(trace.status)}
              <span className={`text-sm font-medium ${getStatusColor(trace.status)}`}>
                {trace.status.toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        {/* Trace Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center gap-3">
              <ClockIcon className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('apm:traces.details.duration')}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatDuration(trace.duration_ms * 1000)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center gap-3">
              <ServerIcon className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('apm:traces.details.spans')}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{trace.span_count}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center gap-3">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('apm:traces.details.errors')}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{trace.error_count}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center gap-3">
              <ClockIcon className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('apm:traces.details.timestamp')}</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {new Date(typeof trace.timestamp === 'number' ? trace.timestamp : parseInt(trace.timestamp as any)).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Waterfall View */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('apm:traces.details.spanTimeline')}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t('apm:traces.details.spanTimelineDesc')}
            </p>
          </div>

          <div className="p-6">
            {spans.length === 0 ? (
              <p className="text-center text-gray-600 dark:text-gray-400 py-8">{t('apm:traces.details.noSpans')}</p>
            ) : (
              <div className="space-y-2">
                {spans.map((span: any) => {
                  const { left, width } = calculateWaterfallPosition(span);
                  const isExpanded = expandedSpans.has(span.span_id);
                  const isDatabaseSpan = isDatabase(span);

                  return (
                    <div key={span.span_id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      {/* Span Bar */}
                      <div
                        className="relative cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                        onClick={() => toggleSpan(span.span_id)}
                        style={{ paddingLeft: `${(span.depth || 0) * 24 + 8}px` }}
                      >
                        <div className="py-3 pr-4 flex items-center gap-3">
                          {/* Operation Name */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {isDatabaseSpan && <CircleStackIcon className="h-4 w-4 text-blue-500 flex-shrink-0" />}
                              <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {span.operation_name}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getSpanColor(span.span_kind)}`}>
                                {span.span_kind}
                              </span>
                            </div>
                          </div>

                          {/* Duration */}
                          <div className="flex-shrink-0 text-sm text-gray-600 dark:text-gray-400">
                            {formatDuration(span.duration_us)}
                          </div>

                          {/* Status */}
                          <div className="flex-shrink-0">
                            {getStatusIcon(span.status_code)}
                          </div>
                        </div>

                        {/* Waterfall Bar */}
                        <div className="h-2 bg-gray-100 dark:bg-gray-900 relative">
                          <div
                            className={`absolute h-full border-l-2 ${
                              span.status_code === 'error'
                                ? 'bg-red-400 border-red-600'
                                : 'bg-blue-400 border-blue-600'
                            }`}
                            style={{
                              left: `${left}%`,
                              width: `${width}%`,
                            }}
                          />
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="p-4 bg-gray-50 dark:bg-gray-750 border-t border-gray-200 dark:border-gray-700">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Attributes */}
                            {span.attributes && Object.keys(span.attributes).length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Attributes</h4>
                                <div className="space-y-1">
                                  {Object.entries(span.attributes).map(([key, value]) => (
                                    <div key={key} className="flex items-start gap-2 text-xs">
                                      <span className="font-mono text-gray-600 dark:text-gray-400 min-w-[120px]">{key}:</span>
                                      <span className="font-mono text-gray-900 dark:text-white break-all">
                                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Metadata */}
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Metadata</h4>
                              <div className="space-y-1 text-xs">
                                <div><span className="text-gray-600 dark:text-gray-400">Span ID:</span> <span className="font-mono">{span.span_id}</span></div>
                                {span.parent_span_id && <div><span className="text-gray-600 dark:text-gray-400">Parent ID:</span> <span className="font-mono">{span.parent_span_id}</span></div>}
                                <div><span className="text-gray-600 dark:text-gray-400">Start:</span> {new Date(typeof span.start_time === 'number' ? span.start_time : span.start_time).toLocaleString()}</div>
                                {span.status_message && (
                                  <div className="text-red-600 dark:text-red-400">
                                    <span className="text-gray-600 dark:text-gray-400">Error:</span> {span.status_message}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Events */}
                          {span.events && span.events.length > 0 && (
                            <div className="mt-4">
                              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Events</h4>
                              <div className="space-y-2">
                                {span.events.map((event: any, idx: number) => (
                                  <div key={idx} className="bg-white dark:bg-gray-800 p-2 rounded text-xs">
                                    <div className="font-semibold">{event.name}</div>
                                    {event.attributes && (
                                      <pre className="mt-1 text-gray-600 dark:text-gray-400 overflow-auto">
                                        {JSON.stringify(event.attributes, null, 2)}
                                      </pre>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Database Queries Section */}
        {spans.some(s => isDatabase(s)) && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <CircleStackIcon className="h-6 w-6 text-blue-500" />
                Database Queries
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {spans.filter(s => isDatabase(s)).map((span: any) => (
                  <div key={span.span_id} className="bg-gray-50 dark:bg-gray-750 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm text-gray-900 dark:text-white mb-1">{span.operation_name}</div>
                        {span.attributes?.['db.statement'] && (
                          <pre className="text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 p-2 rounded whitespace-pre-wrap break-words overflow-wrap-anywhere">
                            {span.attributes['db.statement']}
                          </pre>
                        )}
                      </div>
                      <div className="ml-4 text-right flex-shrink-0">
                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                          {formatDuration(span.duration_us)}
                        </div>
                        {span.attributes?.['db.system'] && (
                          <div className="text-xs text-gray-600 dark:text-gray-400">{span.attributes['db.system']}</div>
                        )}
                      </div>
                    </div>
                    {span.attributes?.['db.name'] && (
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        Database: {span.attributes['db.name']}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
